import React, { useMemo, useState } from "react";
import { ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";

/**
 * useSortable — generic client-side sort hook for arrays of objects.
 *
 *   const { sortedRows, sortKey, sortDir, requestSort } = useSortable(rows, "sales", "desc");
 *
 *   <th><SortHeader label="Ventas" sortKey="sales" .../></th>
 *
 *   accessor(row) → number | string used for comparing (defaults to row[key]).
 *   Provide an accessor when the value is nested (e.g. "totals.sales").
 */
export function useSortable(rows, initialKey = null, initialDir = "desc") {
  const [sortKey, setSortKey] = useState(initialKey);
  const [sortDir, setSortDir] = useState(initialDir);
  const [accessor, setAccessor] = useState(() => null);

  const requestSort = (key, opts = {}) => {
    setAccessor(() => opts.accessor || null);
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(opts.defaultDir || "desc");
    }
  };

  const getValue = (row) => {
    if (accessor) return accessor(row);
    if (!sortKey) return null;
    // dotted path support: "totals.sales"
    if (sortKey.includes(".")) {
      return sortKey.split(".").reduce((o, k) => (o == null ? null : o[k]), row);
    }
    return row?.[sortKey];
  };

  const sortedRows = useMemo(() => {
    if (!sortKey || !Array.isArray(rows)) return rows;
    const dir = sortDir === "asc" ? 1 : -1;
    const arr = [...rows];
    arr.sort((a, b) => {
      const va = getValue(a);
      const vb = getValue(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") {
        return (va - vb) * dir;
      }
      return String(va).localeCompare(String(vb), "es", { numeric: true, sensitivity: "base" }) * dir;
    });
    return arr;
  }, [rows, sortKey, sortDir, accessor]); // eslint-disable-line react-hooks/exhaustive-deps

  return { sortedRows, sortKey, sortDir, requestSort };
}

/**
 * SortHeader — clickable column header that shows a sort icon.
 * Use inside <th>:
 *   <th><SortHeader label="Ventas" k="sales" sort={sort} num /></th>
 */
export function SortHeader({ label, k, sort, num = false, accessor, defaultDir, testid }) {
  const active = sort.sortKey === k;
  const dir = sort.sortDir;
  return (
    <button
      type="button"
      onClick={() => sort.requestSort(k, { accessor, defaultDir })}
      data-testid={testid || `sort-${k}`}
      className={
        "inline-flex items-center gap-1 select-none cursor-pointer hover:opacity-70 transition " +
        (active ? "font-semibold" : "") + " " +
        (num ? "justify-end w-full" : "")
      }
      title="Ordenar"
    >
      <span>{label}</span>
      {active ? (
        dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />
      ) : (
        <ChevronsUpDown size={11} className="opacity-40" />
      )}
    </button>
  );
}
